// Населени места на България (5 252 града и села) — области, общини и
// помощни функции за търсене/етикети. Данните са в data/settlements.json:
// { n: име, t: 'град' | 'село', m: община, p: област }, подредени по област,
// градовете първи.

import settlements from './data/settlements.json';

export type Settlement = { n: string; t: 'град' | 'село'; m: string; p: string };
export type Place = { city: string; region: string; municipality: string };

const ALL = settlements as Settlement[];

// 28-те области в официалния ред
export const REGIONS: string[] = [
  'Благоевград',
  'Бургас',
  'Варна',
  'Велико Търново',
  'Видин',
  'Враца',
  'Габрово',
  'Добрич',
  'Кърджали',
  'Кюстендил',
  'Ловеч',
  'Монтана',
  'Пазарджик',
  'Перник',
  'Плевен',
  'Пловдив',
  'Разград',
  'Русе',
  'Силистра',
  'Сливен',
  'Смолян',
  'Софийска',
  'София-град',
  'Стара Загора',
  'Търговище',
  'Хасково',
  'Шумен',
  'Ямбол',
];

// Индекси: по област (запазват реда от файла — градовете първи) и по име
const byRegion = new Map<string, Settlement[]>();
const byName = new Map<string, Settlement[]>();
for (const s of ALL) {
  let list = byRegion.get(s.p);
  if (!list) byRegion.set(s.p, (list = []));
  list.push(s);
  const key = s.n.toLocaleLowerCase('bg-BG');
  let named = byName.get(key);
  if (!named) byName.set(key, (named = []));
  named.push(s);
}

const norm = (v: string) => v.trim().toLocaleLowerCase('bg-BG');

// Маха „гр.“/„с.“/„град“/„село“ от началото на заявката („гр. Плов“ → „плов“)
const stripPrefix = (q: string) => norm(q).replace(/^(гр\.?|с\.?|град|село)\s+/u, '').replace(/^(гр\.|с\.)/u, '').trim();

// Общината без излишното „община“ („Столична община“ → „Столична“)
const shortMunicipality = (m: string) => m.replace(/\s*община\s*/iu, ' ').trim();

export function settlementsIn(region: string): Settlement[] {
  return byRegion.get(region) ?? [];
}

// Търсене в област: без букви — целият списък; иначе съвпадение по подниз,
// като тези, които ЗАПОЧВАТ със заявката, са отпред.
export function searchSettlements(region: string, query: string): Settlement[] {
  const list = settlementsIn(region);
  const q = stripPrefix(query);
  if (!q) return list;
  const starts: Settlement[] = [];
  const contains: Settlement[] = [];
  for (const s of list) {
    const n = norm(s.n);
    if (n.startsWith(q)) starts.push(s);
    else if (n.includes(q)) contains.push(s);
  }
  return starts.concat(contains);
}

// „гр. Пловдив“ / „с. Труд, общ. Марица“
export function settlementLabel(s: Settlement): string {
  return s.t === 'град' ? `гр. ${s.n}` : `с. ${s.n}, общ. ${shortMunicipality(s.m)}`;
}

// Пълен етикет с областта — за полето в редакцията
export function settlementFullLabel(s: Settlement): string {
  return `${settlementLabel(s)}, обл. ${s.p}`;
}

export function toPlace(s: Settlement): Place {
  return { city: s.n, region: s.p, municipality: s.m };
}

// Намира населено място по име (и област, ако е известна). Без област:
// уникално име → него; иначе единственият ГРАД с това име (старите записи
// идват от списък само с градове); иначе null (двусмислено).
export function findSettlement(name: string | null | undefined, region?: string | null): Settlement | null {
  if (!name) return null;
  const matches = byName.get(norm(name));
  if (!matches?.length) return null;
  if (region) return matches.find((s) => s.p === region) ?? null;
  if (matches.length === 1) return matches[0];
  const towns = matches.filter((s) => s.t === 'град');
  return towns.length === 1 ? towns[0] : null;
}

// Областта на град по име — само когато е еднозначно (вж. findSettlement)
export function regionOfCity(name: string | null | undefined): string | null {
  return findSettlement(name)?.p ?? null;
}

// За показване в карти/заглавия: селата — „с. Труд, общ. Марица“,
// градовете — само името (както досега). Непознато име → както е.
export function cityLabel(city: string | null | undefined, region?: string | null): string {
  if (!city) return '';
  const s = findSettlement(city, region);
  return s && s.t === 'село' ? settlementLabel(s) : city;
}

// Като cityLabel, но и градовете са с „гр.“ (за екрани, които вече го пишат)
export function placeLabel(city: string | null | undefined, region?: string | null): string {
  if (!city) return '';
  const s = findSettlement(city, region);
  return s ? settlementLabel(s) : `гр. ${city}`;
}
